import axios, { AxiosError } from 'axios';
import { InputCheckoutProps } from '../@types/ServiceCheckoutInput';
import { CheckoutResponseDTO } from '../DTO/checkoutResponseDTO';
import { CampaignResponseDTO } from '../DTO/campaignResponseDTO';
import { ProductTypeResponseDTO } from '../DTO/productResponseDTO';
import { ShopDTO } from '../DTO/shopResponseDTO';
import { axiosClient } from '../network/appClient';
import { useToken } from '../store/auth/tokenStore';

type PropsGetShopItemsService = {
    Date: string;
    ProductId?: number;
    ProductTypeId?: number;
    ProductWaveSide?: 'Left' | 'Right';
    ProductTypeCategory?:
        | 'Waves'
        | 'Tickets'
        | 'Park'
        | 'Services'
        | 'Sports'
        | 'Others';
    isFirstHeat?: boolean;
};

type GetItemsShopInterface = {
    (props: PropsGetShopItemsService): Promise<ShopDTO[]>;
};

export const GetItemsShop: GetItemsShopInterface = async props => {
    try {
        const response = await axiosClient.get('Sale/AvailableProducts', {
            params: {
                ...props,
                isFirstHeat: undefined,
                allowGifts: props.isFirstHeat,
            },
            validateStatus: () => true,
        });

        if (response.status === 200) {
            return response.data as ShopDTO[];
        }

        if (response.status === 404) {
            return [];
        }

        throw new Error('Falha ao pegar os dados do shopping de vendas');
    } catch (error) {
        if (error instanceof AxiosError && error.code === 'ERR_CANCELED') {
            return [];
        }
        throw new Error('Falha ao pegar os dados do shopping de vendas!');
    }
};

type GetProductTypeListInterface = {
    (): Promise<ProductTypeResponseDTO[]>;
};

export const ProductTypeList: GetProductTypeListInterface = async () => {
    try {
        const response = await axiosClient.get(
            'Sale/AvailableProductTypes'
        );

        if (response.status === 200) {
            return response.data as ProductTypeResponseDTO[];
        } else {
            throw new Error('Falha ao pegar os tipos de produtos');
        }
    } catch (error) {
        throw new Error('Falha ao pegar os tipos de produtos');
    }
};

type CheckoutInterface = {
    (props: InputCheckoutProps): Promise<CheckoutResponseDTO>;
};

export const Checkout: CheckoutInterface = async props => {
    try {
        const response = await axiosClient.post(
            'Sale/Checkout',
            {
                ...props,
            },
            {
                headers: {
                    Authorization: `Bearer ${useToken.getState().token}`,
                },
            }
        );

        return response.data as CheckoutResponseDTO;
    } catch (error) {
        if (error instanceof AxiosError && error.response?.data?.parameter) {
            const param = error.response?.data?.parameter as string;
            if (param === 'DiscountCoupon.UnauthorizedUse') {
                throw new Error('Cupom não pode ser usado neste contexto');
            }
            throw new Error(param);
        }
        throw new Error('Falha ao seguir para o pagamento');
    }
};

const getCardBrands: unknown = async () => {
    try {
        const response = await axiosClient.get('Sale/CardBrands', {
            headers: {
                Authorization: `Bearer ${useToken.getState().token}`,
            },
        });

        return response.data as unknown;
    } catch (error) {
        if (error instanceof AxiosError && error.response?.data?.parameter) {
            throw new Error(error.response?.data?.parameter);
        }
        throw new Error('Falha ao seguir para o pagamento');
    }
};

export type CountryCodesDTO = {
    id: number;
    name: string;
    iso2: string;
    iso3: string;
    ddi: string;
}[];

const fetchCountryCodes = async () => {
    const response = await axiosClient.get('Country/List');

    if (response.status === 200) {
        return (response.data as CountryCodesDTO).sort((a, b) =>
            a.name.localeCompare(b.name)
        );
    }

    return response.data as CountryCodesDTO;
};

interface PaymentLinkResponseDTO {
    id: number;
    token: string;
    paymentInExternalLink: boolean;
    person: {
        personId: number;
        token: string;
        name: string;
        document: string;
        email: string;
        phone: string;
        birthDate: Date;
        foreigner: boolean;
        lastLoginAt: Date;
    };
    used: boolean;
    statusId: number;
    amount: number;
    value: number;
    products: [
        {
            id: number;
            token: string;
            used: boolean;
            date: Date;
            product: {
                id: number;
                name: string;
                exhibitionName: string;
                image: string;
                description: string;
                information1: string;
                information2: string;
                orderBy: number;
                active: boolean;
                videoUrl: string;
                waveSide: string;
                waveLevel: string;
                waveLevelAcceptBay: boolean;
                partnerId: string;
                backOfficePartnerId: string;
            };
            schedule: {
                id: number;
                name: string;
                active: boolean;
                monday: boolean;
                tuesday: boolean;
                wednesday: boolean;
                thursday: boolean;
                friday: boolean;
                saturday: boolean;
                sunday: boolean;
            };
            value: number;
            voucherUrl: string;
            statusId: number;
            createdAt: Date;
            client: {
                id: number;
                name: string;
                document: string;
            };
        },
    ];
    createdAt: Date;
}

const getPaymentLink = async (token: string) => {
    try {
        const response = await axiosClient.get<PaymentLinkResponseDTO>(
            '/Sale/PaymentInExternalLink?' +
                new URLSearchParams({ Token: token }).toString(),
            {
                headers: {
                    Authorization: `Bearer ${useToken.getState().token}`,
                },
                // validateStatus: () => true
            }
        );

        return response.data;
    } catch (error) {
        if (error instanceof AxiosError && error.response?.data?.parameter) {
            throw new Error(error.response?.data?.parameter);
        }
        throw new Error('Falha ao buscar dados desse pagamento');
    }
};

interface SearchCepDataDTO {
    cep: string;
    logradouro: string;
    bairro: string;
    localidade: string;
    uf: string;
    estado: string;
    regiao: string;
}

const fetchAddressDataByCep = async (cep: string) => {
    try {
        const response = await axios.get<SearchCepDataDTO>(
            `https://viacep.com.br/ws/${cep}/json/`,
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );

        return response.data;
    } catch (error) {
        if (error instanceof AxiosError && error.response?.data?.parameter) {
            throw new Error(error.response?.data?.parameter);
        }
        throw new Error('Falha ao buscar dados desse pagamento');
    }
};

const shopService = {
    getCardBrands,
    getPaymentLink,
    fetchCountryCodes,
    fetchAddressDataByCep,
};

export default shopService;

export async function getCampaign( date : string): Promise<CampaignResponseDTO | null> {
    try {
        const response = await axiosClient.get(
            `Sale/Campaign?Date=${date}`
        );

        return response.data as CampaignResponseDTO;
    } catch (error) {
        return null
    }
}